/**
 * Premium Collection Facets Handler
 * 
 * Verantwoordelijk voor het afhandelen van filtering en sortering
 * in de geavanceerde collectiepagina.
 */

class FacetsHandler {
  constructor() {
    this.selectors = {
      facetForm: '#FacetFiltersForm',
      mobileFilterForm: '#MobileFilterForm',
      facetLinks: '.js-facet-link',
      facetWrapper: '#main-collection-filters',
      facetSort: '.collection-filters__sort',
      productGrid: '#product-grid',
      productGridContainer: '#ProductGridContainer',
      totalResults: '[data-total-results]',
      filterSidebar: '.filter-sidebar',
      filterSections: '.filter-section__title',
      filterOptions: '.filter-option__label input',
      priceMinInput: '#price-min',
      priceMaxInput: '#price-max',
      priceApplyBtn: '.price-apply-btn',
      clearFiltersBtn: '.clear-filters-btn',
      mobileFilterBar: '.mobile-filter-bar',
      mobileFilterToggle: '.mobile-filter-toggle',
      mobileSortToggle: '.mobile-sort-toggle',
      mobileFilterDrawer: '#mobile-filter-drawer',
      mobileSortDrawer: '#mobile-sort-drawer',
      mobileFilterClose: '.mobile-filter-drawer__close',
      mobileFilterApply: '.mobile-filter-drawer__apply',
      mobileFilterReset: '.mobile-filter-drawer__reset',
      mobileFilterOverlay: '.mobile-filter-overlay',
      viewOptions: '.view-option',
      quickAddButtons: '.product-card__quick-add',
      wishlistButtons: '.product-card__wishlist'
    };

    this.state = {
      currentUrl: window.location.href,
      searchParams: new URLSearchParams(window.location.search),
      activeDrawer: null,
      activeFilters: {}
    };

    this.bindEvents();
    this.initializeState();
  }

  /**
   * Initialiseer de huidige staat en UI
   */
  initializeState() {
    // Initialiseer filter secties
    this.initializeFilterSections();
    
    // Vul filter formulier met huidige URL parameters
    this.populateFilterFormFromUrl();
    
    // Update mobiele filter count
    this.updateMobileFilterCount();
  }

  /**
   * Initialiseer filter secties (uitklapbaar)
   */
  initializeFilterSections() {
    const filterSections = document.querySelectorAll(this.selectors.filterSections);
    
    filterSections.forEach(section => {
      const targetId = section.getAttribute('data-target');
      if (!targetId) return;
      
      const targetContent = document.getElementById(targetId.substring(1));
      if (!targetContent) return;

      // Start with all sections expanded
      section.addEventListener('click', () => {
        section.classList.toggle('collapsed');
        targetContent.classList.toggle('collapsed');
      });
    });
  }

  /**
   * Vul filter formulier met huidige URL parameters
   */
  populateFilterFormFromUrl() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    
    // Voor elke parameter, vind de overeenkomstige input en zet de waarde
    params.forEach((value, key) => {
      // Prijs filters
      if (key.includes('price.gte')) {
        const minInput = document.querySelector(this.selectors.priceMinInput);
        if (minInput) minInput.value = value;
      }
      else if (key.includes('price.lte')) {
        const maxInput = document.querySelector(this.selectors.priceMaxInput);
        if (maxInput) maxInput.value = value;
      }
      // Checkbox filters
      else {
        const checkbox = document.querySelector(`.filter-option__label input[name="${key}"][value="${value}"]`);
        if (checkbox) checkbox.checked = true;
      }
    });
  }

  /**
   * Registreer event listeners
   */
  bindEvents() {
    // Filter opties (checkboxes)
    const filterOptions = document.querySelectorAll(this.selectors.filterOptions);
    filterOptions.forEach(option => {
      option.addEventListener('change', this.onFilterChange.bind(this));
    });
    
    // Prijs filter toepassen knop
    const priceApplyBtn = document.querySelector(this.selectors.priceApplyBtn);
    if (priceApplyBtn) {
      priceApplyBtn.addEventListener('click', this.onPriceFilterApply.bind(this));
    }
    
    // Clear filters knop
    const clearFiltersBtn = document.querySelector(this.selectors.clearFiltersBtn);
    if (clearFiltersBtn) {
      clearFiltersBtn.addEventListener('click', this.onClearFilters.bind(this));
    }

    // Mobiele filter toggle
    const mobileFilterToggle = document.querySelector(this.selectors.mobileFilterToggle);
    if (mobileFilterToggle) {
      mobileFilterToggle.addEventListener('click', () => {
        this.openDrawer('filter');
      });
    }
    
    // Mobiele sort toggle
    const mobileSortToggle = document.querySelector(this.selectors.mobileSortToggle);
    if (mobileSortToggle) {
      mobileSortToggle.addEventListener('click', () => {
        this.openDrawer('sort');
      });
    }
    
    // Mobiele filter/sort sluit knoppen
    const mobileFilterCloseButtons = document.querySelectorAll(this.selectors.mobileFilterClose);
    mobileFilterCloseButtons.forEach(button => {
      button.addEventListener('click', this.closeDrawer.bind(this));
    });
    
    // Mobiele filter overlay
    const mobileFilterOverlay = document.querySelector(this.selectors.mobileFilterOverlay);
    if (mobileFilterOverlay) {
      mobileFilterOverlay.addEventListener('click', this.closeDrawer.bind(this));
    }
    
    // Mobiele filter toepassen
    const mobileFilterApplyButtons = document.querySelectorAll(this.selectors.mobileFilterApply);
    mobileFilterApplyButtons.forEach(button => {
      button.addEventListener('click', this.applyMobileFilters.bind(this));
    });
    
    // Mobiele filter reset
    const mobileFilterResetButton = document.querySelector(this.selectors.mobileFilterReset);
    if (mobileFilterResetButton) {
      mobileFilterResetButton.addEventListener('click', this.resetMobileFilters.bind(this));
    }

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', this.onHistoryChange.bind(this));
    
    // Luister naar resize events om de drawer te sluiten bij desktop view
    window.addEventListener('resize', this.handleResize.bind(this));
  }
  
  /**
   * Wanneer een filter optie (checkbox) verandert
   */
  onFilterChange(event) {
    const checkbox = event.target;
    const name = checkbox.getAttribute('name');
    const value = checkbox.value;
    const checked = checkbox.checked;
    
    // Update URL met filter parameters
    const url = new URL(window.location.href);
    
    if (checked) {
      url.searchParams.set(name, value);
    } else {
      url.searchParams.delete(name);
    }
    
    // Update URL en historie zonder pagina opnieuw te laden
    history.pushState({ searchParams: url.searchParams.toString() }, '', url);
    
    // Haal de gefilterde resultaten op
    this.renderPage(url.toString());
  }
  
  /**
   * Wanneer prijs filter wordt toegepast
   */
  onPriceFilterApply() {
    const minInput = document.querySelector(this.selectors.priceMinInput);
    const maxInput = document.querySelector(this.selectors.priceMaxInput);
    
    if (!minInput || !maxInput) return;
    
    const url = new URL(window.location.href);
    
    // Update URL met prijs parameters
    if (minInput.value) {
      url.searchParams.set(minInput.name, minInput.value);
    } else {
      url.searchParams.delete(minInput.name);
    }
    
    if (maxInput.value) {
      url.searchParams.set(maxInput.name, maxInput.value);
    } else {
      url.searchParams.delete(maxInput.name);
    }
    
    // Update URL en historie zonder pagina opnieuw te laden
    history.pushState({ searchParams: url.searchParams.toString() }, '', url);
    
    // Haal de gefilterde resultaten op
    this.renderPage(url.toString());
  }
  
  /**
   * Wanneer alle filters worden gereset
   */
  onClearFilters() {
    // Reset alle checkboxes
    const checkboxes = document.querySelectorAll(this.selectors.filterOptions);
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Reset prijs inputs
    const minInput = document.querySelector(this.selectors.priceMinInput);
    const maxInput = document.querySelector(this.selectors.priceMaxInput);
    
    if (minInput) minInput.value = '';
    if (maxInput) maxInput.value = '';
    
    // Reset URL naar basis URL zonder filters
    const url = new URL(window.location.origin + window.location.pathname);
    
    // Behoud eventuele sort parameter
    const sortParam = new URL(window.location.href).searchParams.get('sort_by');
    if (sortParam) {
      url.searchParams.set('sort_by', sortParam);
    }
    
    // Update URL en historie zonder pagina opnieuw te laden
    history.pushState({ searchParams: url.searchParams.toString() }, '', url);
    
    // Haal de ongefilterde resultaten op
    this.renderPage(url.toString());
  }

  /**
   * Open filter of sort drawer
   */
  openDrawer(type) {
    // Sluit een eventueel open drawer
    this.closeDrawer();
    
    // Activeer de overlay
    const overlay = document.querySelector(this.selectors.mobileFilterOverlay);
    if (overlay) {
      overlay.classList.add('active');
    }
    
    // Activeer de juiste drawer
    let drawer;
    if (type === 'filter') {
      drawer = document.querySelector(this.selectors.mobileFilterDrawer);
    } else if (type === 'sort') {
      drawer = document.querySelector(this.selectors.mobileSortDrawer);
    }
    
    if (drawer) {
      drawer.classList.add('active');
      drawer.setAttribute('aria-hidden', 'false');
      this.state.activeDrawer = type;
      
      // Voorkom scrollen van de pagina
      document.body.style.overflow = 'hidden';
    }
  }
  
  /**
   * Sluit de active drawer
   */
  closeDrawer() {
    const overlay = document.querySelector(this.selectors.mobileFilterOverlay);
    const filterDrawer = document.querySelector(this.selectors.mobileFilterDrawer);
    const sortDrawer = document.querySelector(this.selectors.mobileSortDrawer);
    
    if (overlay) {
      overlay.classList.remove('active');
    }
    
    if (filterDrawer) {
      filterDrawer.classList.remove('active');
      filterDrawer.setAttribute('aria-hidden', 'true');
    }
    
    if (sortDrawer) {
      sortDrawer.classList.remove('active');
      sortDrawer.setAttribute('aria-hidden', 'true');
    }
    
    this.state.activeDrawer = null;
    
    // Herstel scrollen
    document.body.style.overflow = '';
  }
  
  /**
   * Pas filters toe vanuit de mobiele drawer
   */
  applyMobileFilters(event) {
    // Bepaal welke drawer is actief
    const parentDrawer = event.target.closest('.mobile-filter-drawer');
    
    if (!parentDrawer) return;
    
    // Filter toepassen
    if (parentDrawer.id === 'mobile-filter-drawer') {
      const form = document.querySelector(this.selectors.mobileFilterForm);
      if (!form) return;
      
      // Verzamel alle actieve filters
      const formData = new FormData(form);
      const searchParams = new URLSearchParams(formData);
      
      // Bouw URL op met actieve filters
      const newUrl = new URL(window.location.href);
      newUrl.search = searchParams.toString();
      
      // Update URL en historie zonder pagina opnieuw te laden
      history.pushState({ searchParams: searchParams.toString() }, '', newUrl);
      
      // Haal de gefilterde resultaten op
      this.renderPage(newUrl.toString());
    } 
    // Sorteren toepassen
    else if (parentDrawer.id === 'mobile-sort-drawer') {
      const sortInputs = parentDrawer.querySelectorAll('input[name="sort_by"]');
      let selectedSort = '';
      
      sortInputs.forEach(input => {
        if (input.checked) {
          selectedSort = input.value;
        }
      });
      
      if (selectedSort) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', selectedSort);
        
        // Update URL en historie zonder pagina opnieuw te laden
        history.pushState({ searchParams: url.searchParams.toString() }, '', url);
        
        // Haal de gesorteerde resultaten op
        this.renderPage(url.toString());
      }
    }
    
    // Sluit de drawer
    this.closeDrawer();
  }
  
  /**
   * Reset alle filters in mobiele drawer
   */
  resetMobileFilters() {
    const form = document.querySelector(this.selectors.mobileFilterForm);
    if (!form) return;
    
    // Reset alle form elementen
    form.reset();
    
    // Ga naar de basis URL zonder filters
    const url = new URL(window.location.origin + window.location.pathname);
    
    // Update URL en historie zonder pagina opnieuw te laden
    history.pushState({ searchParams: '' }, '', url);
    
    // Haal de ongefilterde resultaten op
    this.renderPage(url.toString());
    
    // Sluit de drawer
    this.closeDrawer();
  }
  
  /**
   * Handle resize event om drawer te sluiten bij desktop view
   */
  handleResize() {
    if (window.innerWidth > 991 && this.state.activeDrawer) {
      this.closeDrawer();
    }
  }
  
  /**
   * Update de mobiele filter count
   */
  updateMobileFilterCount() {
    const countBubble = document.querySelector('.mobile-filter-count');
    if (!countBubble) return;
    
    // Tel het aantal actieve filters
    const url = new URL(window.location.href);
    const params = url.searchParams;
    let count = 0;
    
    // Tel de parameters, maar sla sort_by over
    params.forEach((value, key) => {
      if (key !== 'sort_by') {
        count++;
      }
    });
    
    // Update de count bubble
    if (count > 0) {
      countBubble.textContent = `(${count})`;
      countBubble.style.display = 'inline-block';
    } else {
      countBubble.textContent = '';
      countBubble.style.display = 'none';
    }
  }

  /**
   * Wanneer browser geschiedenis verandert (back/forward)
   */
  onHistoryChange(event) {
    // Haal de gefilterde resultaten op
    this.renderPage(window.location.href);
    
    // Update filter formulier met nieuwe URL parameters
    this.populateFilterFormFromUrl();
  }

  /**
   * Gebruik Fetch API om pagina-inhoud bij te werken zonder volledige pagina-refresh
   */
  async renderPage(url) {
    this.startLoading();
    
    try {
      const response = await fetch(url);
      const data = await response.text();
      
      // Parse de HTML respons
      const html = new DOMParser().parseFromString(data, 'text/html');
      
      // Update product grid
      this.updateProductGrid(html);
      
      // Update actieve filters
      this.updateMobileFilterCount();
      
      this.state.currentUrl = url;
    } catch (error) {
      console.error('Error updating content:', error);
    } finally {
      this.stopLoading();
    }
  }

  /**
   * Update de productgrid met nieuwe resultaten
   */
  updateProductGrid(html) {
    const newProductGrid = html.querySelector(this.selectors.productGrid);
    const currentProductGrid = document.querySelector(this.selectors.productGrid);
    
    if (newProductGrid && currentProductGrid) {
      currentProductGrid.innerHTML = newProductGrid.innerHTML;
      
      // Herbind events voor nieuwe knoppen
      this.bindNewProductEvents();
    }
    
    // Update pagination
    const newPagination = html.querySelector('.pagination');
    const currentPagination = document.querySelector('.pagination');
    
    if (newPagination && currentPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
    }
  }

  /**
   * Herbind events voor nieuw gerenderde producten
   */
  bindNewProductEvents() {
    // Hier code toevoegen indien nodig voor productinteracties
  }

  /**
   * Toon laad-indicator
   */
  startLoading() {
    const container = document.querySelector(this.selectors.productGridContainer);
    if (!container) return;
    
    container.classList.add('is-loading');
    
    // Voeg loading overlay toe indien nog niet aanwezig
    if (!container.querySelector('.loading-overlay')) {
      const loadingOverlay = document.createElement('div');
      loadingOverlay.className = 'loading-overlay';
      loadingOverlay.innerHTML = `
        <div class="loading-overlay__spinner">
          <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      `;
      container.appendChild(loadingOverlay);
    } else {
      container.querySelector('.loading-overlay').style.display = 'block';
    }
  }

  /**
   * Verberg laad-indicator
   */
  stopLoading() {
    const container = document.querySelector(this.selectors.productGridContainer);
    if (!container) return;
    
    container.classList.remove('is-loading');
    
    const loadingOverlay = container.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
    }
  }
}

// Initialiseer de facets handler wanneer de DOM geladen is
document.addEventListener('DOMContentLoaded', () => {
  new FacetsHandler();
});